import React, { useState, useEffect, useMemo } from "react";
import { getAvatarUrl, subscribeToAvatarUpdates, preloadAvatars } from "../utils/avatarCache";
import { fetchGoogleContactPhotos } from "../utils/googleContactPhotos";

interface AvatarProps {
  email: string | null;
  name?: string | null;
  profilePhoto?: string | null; // Uploaded photo (base64) - highest priority
  size?: number;
  className?: string;
  fallbackClassName?: string;
  fallbackContent?: React.ReactNode;
}

interface AvatarState {
  trackedEmail: string | null;
  trackedProfilePhoto: string | null | undefined;
  avatarState: ReturnType<typeof getAvatarUrl>;
  photoError: boolean;
}

/**
 * Avatar component that displays uploaded photo, Gravatar, Google photo, or initials fallback.
 * Priority: profilePhoto > Gravatar > Google Contact photo > initials
 * Automatically updates when avatar cache is populated.
 */
const Avatar: React.FC<AvatarProps> = ({
  email,
  name,
  profilePhoto,
  size = 40,
  className = "rounded",
  fallbackClassName = "rounded bg-gradient-to-br from-navy-light to-navy-lighter",
  fallbackContent,
}) => {
  // Combined state to track prop changes
  const [state, setState] = useState<AvatarState>(() => ({
    trackedEmail: email,
    trackedProfilePhoto: profilePhoto,
    avatarState: getAvatarUrl(email),
    photoError: false,
  }));

  // Reset state when props change (setState during render is allowed in React 18)
  if (state.trackedEmail !== email) {
    setState((prev) => ({
      ...prev,
      trackedEmail: email,
      avatarState: getAvatarUrl(email),
    }));
  }

  if (state.trackedProfilePhoto !== profilePhoto) {
    setState((prev) => ({
      ...prev,
      trackedProfilePhoto: profilePhoto,
      photoError: false,
    }));
  }

  // Subscribe to cache updates for this email
  useEffect(() => {
    if (!email) return;

    const normalizedEmail = email.toLowerCase().trim();

    // Update state when cache changes (this is in a callback, not synchronous)
    const unsubscribe = subscribeToAvatarUpdates((updatedEmail) => {
      if (updatedEmail === normalizedEmail) {
        setState((prev) => ({
          ...prev,
          avatarState: getAvatarUrl(email),
        }));
      }
    });

    return unsubscribe;
  }, [email]);

  // Get initials for fallback
  const getInitials = (): string => {
    if (name) {
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return (parts[0]?.[0] || "?").toUpperCase();
    }
    if (email) {
      return email[0].toUpperCase();
    }
    return "?";
  };

  const defaultFallback = <span className="text-white text-sm font-medium">{getInitials()}</span>;

  // Priority 1: Show uploaded profile photo if available
  if (profilePhoto && !state.photoError) {
    return (
      <img
        src={profilePhoto}
        alt={name || ""}
        className={`object-cover ${className}`}
        style={{ width: size, height: size }}
        onError={() => setState((prev) => ({ ...prev, photoError: true }))}
      />
    );
  }

  // Priority 2: Show cached avatar (Gravatar or Google photo)
  if (state.avatarState.url) {
    return (
      <img
        src={state.avatarState.url}
        alt={name || ""}
        className={`object-cover ${className}`}
        style={{ width: size, height: size }}
        referrerPolicy="no-referrer"
        onError={() => {
          // If image fails to load, show fallback
          setState((prev) => ({
            ...prev,
            avatarState: { url: null, type: "none", loading: false },
          }));
        }}
      />
    );
  }

  // Priority 3: Show fallback (initials)
  return (
    <div
      className={`flex items-center justify-center ${fallbackClassName}`}
      style={{ width: size, height: size }}
    >
      {fallbackContent || defaultFallback}
    </div>
  );
};

export default Avatar;

interface PreloaderState {
  trackedEmailsKey: string;
  trackedGooglePhotosCount: number;
  loading: boolean;
  progress: number;
}

/**
 * Hook to preload avatars for a list of contacts.
 * Call this once when contacts are loaded.
 */
export function useAvatarPreloader(
  emails: (string | null)[],
  accessToken: string | null
): { loading: boolean; progress: number } {
  // Create stable keys for dependency tracking
  const emailsKey = useMemo(() => emails.filter(Boolean).sort().join(","), [emails]);
  const [googlePhotos, setGooglePhotos] = useState<Record<string, string>>({});
  const googlePhotosCount = Object.keys(googlePhotos).length;

  // Combined state to track changes
  const [state, setState] = useState<PreloaderState>(() => ({
    trackedEmailsKey: emailsKey,
    trackedGooglePhotosCount: googlePhotosCount,
    loading: emails.length > 0,
    progress: 0,
  }));

  // Reset loading state when inputs change (setState during render)
  if (
    state.trackedEmailsKey !== emailsKey ||
    state.trackedGooglePhotosCount !== googlePhotosCount
  ) {
    setState((prev) => ({
      ...prev,
      trackedEmailsKey: emailsKey,
      trackedGooglePhotosCount: googlePhotosCount,
      loading: emails.length > 0,
      progress: 0,
    }));
  }

  // Fetch Google photos first
  useEffect(() => {
    if (!accessToken) return;

    fetchGoogleContactPhotos(accessToken)
      .then(setGooglePhotos)
      .catch(() => {
        // Silently fail
      });
  }, [accessToken]);

  // Preload avatars when emails or Google photos change
  useEffect(() => {
    if (emails.length === 0) {
      return;
    }

    let cancelled = false;

    preloadAvatars(emails, googlePhotos, {
      concurrency: 10,
      onProgress: (loaded, total) => {
        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            progress: Math.round((loaded / total) * 100),
          }));
        }
      },
    })
      .then(() => {
        if (!cancelled) {
          setState((prev) => ({ ...prev, loading: false, progress: 100 }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState((prev) => ({ ...prev, loading: false }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [emailsKey, googlePhotosCount, emails, googlePhotos]);

  return { loading: state.loading, progress: state.progress };
}

interface UseAvatarState {
  trackedEmail: string | null;
  avatarState: ReturnType<typeof getAvatarUrl>;
}

/**
 * Hook to get avatar URL for a single email with automatic updates
 */
export function useAvatar(email: string | null): {
  url: string | null;
  type: "gravatar" | "google" | "none";
  loading: boolean;
} {
  const [state, setState] = useState<UseAvatarState>(() => ({
    trackedEmail: email,
    avatarState: getAvatarUrl(email),
  }));

  // Reset state when email changes (setState during render)
  if (state.trackedEmail !== email) {
    setState({
      trackedEmail: email,
      avatarState: getAvatarUrl(email),
    });
  }

  useEffect(() => {
    if (!email) return;

    const normalizedEmail = email.toLowerCase().trim();

    // Subscribe to updates (setState in callback is fine)
    const unsubscribe = subscribeToAvatarUpdates((updatedEmail) => {
      if (updatedEmail === normalizedEmail) {
        setState((prev) => ({
          ...prev,
          avatarState: getAvatarUrl(email),
        }));
      }
    });

    return unsubscribe;
  }, [email]);

  return state.avatarState;
}
